import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaStar } from 'react-icons/fa';

const ComparisonTable = ({ comparisonData }) => {
  const { products, attributes, data } = comparisonData;
  
  // Function to highlight the best value for a numeric attribute
  const findBestValue = (attribute) => {
    const numericValues = {};
    
    products.forEach(product => {
      if (data[product] && data[product][attribute] !== undefined) {
        const value = data[product][attribute];
        if (typeof value === 'number') {
          numericValues[product] = value;
        }
      }
    });
    
    if (Object.keys(numericValues).length > 1) {
      // Determine if higher is better (like storage) or lower is better (like price)
      // This is a simplistic approach - might need customization per attribute
      const lowerIsBetter = attribute.toLowerCase().includes('price') || 
                            attribute.toLowerCase().includes('weight');
      
      let bestProduct = Object.keys(numericValues)[0];
      
      Object.keys(numericValues).forEach(product => {
        if (lowerIsBetter) {
          if (numericValues[product] < numericValues[bestProduct]) {
            bestProduct = product;
          }
        } else {
          if (numericValues[product] > numericValues[bestProduct]) {
            bestProduct = product;
          }
        }
      });
      
      return bestProduct;
    }
    
    return null;
  };

  // Function to render a cell value based on its type
  const renderCellValue = (value, product, attribute) => {
    if (value === null || value === undefined) {
      return <span className="na-value">Unknown</span>;
    }
    
    if (typeof value === 'boolean') {
      return value 
        ? <FaCheck className="check-icon" /> 
        : <FaTimes className="times-icon" />;
    }

    // Handle object values to prevent [object Object] display
    if (typeof value === 'object') {
      // If it's an array, join the values
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      
      // If the object contains common text properties, use them
      if (value.text || value.value || value.name || value.description || value.content) {
        const extractedValue = value.text || value.value || value.name || value.description || value.content;
        return <div className="cell-value">{extractedValue}</div>;
      }
      
      // Otherwise, format the object's key-value pairs as plaintext
      let plainText = '';
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          plainText += key + ': ' + value[key] + '\n\n';
        }
      }
      plainText = plainText.trim();
      return <pre className="cell-value">{plainText}</pre>;
    }
    
    const bestProduct = findBestValue(attribute);
    const isBest = bestProduct === product && typeof value === 'number';
    
    return (
      <div className={`cell-value ${isBest ? 'best-value' : ''}`}>
        {value.toString()}
        {isBest && <FaStar className="star-icon" />}
      </div>
    );
  };

  return (
    <div className="comparison-results">
      <h2 className="table-title">Comparison Results</h2>
      
      <div className="table-responsive">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="attribute-header">Attribute</th>
              {products.map((product, index) => (
                <th key={index} className="product-header">
                  {product}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attributes.map((attribute, index) => {
              // Check if at least one product has a value that is not null, undefined, or "Unknown"
              const nonNAFound = products.some(product => {
                const cell = data[product] && data[product][attribute];
                return cell !== null && cell !== undefined && cell !== "Unknown";
              });
              if (!nonNAFound) return null;

              return (
                <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td className="attribute-cell">{attribute}</td>
                  {products.map((product, pIndex) => (
                    <td key={pIndex} className="value-cell">
                      {data[product] && data[product][attribute] !== undefined
                        ? renderCellValue(data[product][attribute], product, attribute)
                        : <span className="na-value">Unknown</span>}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonTable; 